import { toast } from '@/hooks/useToast'
import { markSkipSentry } from '@/lib/chunkLoadError'

/**
 * Recovery for stale-build chunk errors: a same-origin hard navigation that
 * pulls fresh HTML + build manifest, guarded by a per-URL timestamp so a
 * genuinely missing chunk can never cause a reload loop.
 *
 * Shared by useStaleChunkReload (window error path), the GlobalError
 * ErrorBoundary (render path) and XlsxViewerChunkError (next/dynamic `loading`
 * path) so all honour the same guard — otherwise an error observed by several
 * surfaces could double-reload.
 */

const RELOAD_GUARD_PREFIX = '__chunk-reload:'
// Long enough to block a reload loop from a genuinely missing chunk, short
// enough that the next deploy hitting the same URL can recover again.
const RELOAD_GUARD_TTL_MS = 60_000

const currentPagePath = () => `${window.location.pathname}${window.location.search}`

// Reloads started by this page instance, per URL. Module state does not survive
// the hard navigation, so an entry means the reload has not replaced this page
// yet. The cross-reload loop guard is the sessionStorage record: where
// sessionStorage throws (privacy mode / some WebViews) nothing survives the
// reload, so a chunk that stays missing can reload once per page load there.
const inMemoryReloadedAt = new Map<string, number>()

const hasReloadInFlight = (url: string): boolean => {
  const reloadedAt = inMemoryReloadedAt.get(url)
  return reloadedAt !== undefined && Date.now() - reloadedAt < RELOAD_GUARD_TTL_MS
}

const hasRecentlyReloaded = (url: string): boolean => {
  try {
    const stored = sessionStorage.getItem(`${RELOAD_GUARD_PREFIX}${url}`)
    if (!stored) return false
    const reloadedAt = Number(stored)
    return Number.isFinite(reloadedAt) && Date.now() - reloadedAt < RELOAD_GUARD_TTL_MS
  } catch {
    return false
  }
}

const markReloaded = (url: string) => {
  const now = Date.now()
  inMemoryReloadedAt.set(url, now)
  try {
    sessionStorage.setItem(`${RELOAD_GUARD_PREFIX}${url}`, String(now))
  } catch {
    // in-memory fallback above still guards this page instance
  }
}

// Auto-reloading while the user is mid-input would lose their work.
const isUserEditing = (): boolean => {
  const el = document.activeElement
  return (
    el instanceof HTMLInputElement ||
    el instanceof HTMLTextAreaElement ||
    (el instanceof HTMLElement && el.isContentEditable)
  )
}

// Resolves the URL at call time — for the toast path the user may have
// navigated since the toast appeared. Exported so a manual "Refresh" affordance
// (GlobalError fallback button) reloads through the same guard bookkeeping.
export const reloadCurrentPage = () => {
  const url = currentPagePath()
  markReloaded(url)
  window.location.assign(url)
}

/**
 * Outcome of a recovery attempt, so callers can branch their UI / observability:
 *  - 'reloading'   : a hard reload is in flight (this call started it, or an
 *                    earlier failure in this page already did)
 *  - 'toast'       : a click-to-refresh toast was shown (user was editing)
 *  - 'exhausted'   : already reloaded for this URL recently and it still fails —
 *                    reload won't help, so this likely IS a genuinely broken
 *                    deploy / missing asset worth surfacing
 *  - 'offline'     : the device is offline; reload can't fix it
 *  - 'unavailable' : not in a browser (defensive — every caller is client-only)
 */
export type ChunkRecoveryOutcome = 'reloading' | 'toast' | 'exhausted' | 'offline' | 'unavailable'

/**
 * Recover the current page from a stale-build chunk error. Marks the error so
 * sentry.client.config.ts beforeSend drops it, then hard-navigates (or, while
 * the user is typing, shows a click-to-refresh toast instead of stealing their
 * work).
 *
 * Pass `immediate: true` from the GlobalError ErrorBoundary path: a render-time
 * chunk failure has already torn down the subtree (including the toast
 * container) and there is no in-progress input left to preserve, so it must
 * reload rather than dispatch a toast that would render into nothing.
 */
export const recoverFromChunkError = (
  error?: unknown,
  options?: { immediate?: boolean }
): ChunkRecoveryOutcome => {
  // Nothing to reload outside a browser (defensive — every caller is client-only).
  if (typeof window === 'undefined') return 'unavailable'
  // This page already started a reload for this URL — e.g. several lazy panels
  // failed in the same commit. The navigation is still pending, so this is
  // neither a new recovery nor proof that the reload failed.
  if (hasReloadInFlight(currentPagePath())) {
    markSkipSentry(error)
    return 'reloading'
  }
  // A previous page reloaded this URL moments ago and chunks still fail — don't
  // loop. We deliberately do NOT mark skipSentry here: the caller may want to
  // surface this (a chunk that survives a reload is a real missing-asset signal).
  if (hasRecentlyReloaded(currentPagePath())) return 'exhausted'
  // Reload cannot fix anything while offline; flaky networks surface chunk
  // timeouts as ChunkLoadError too.
  if (!navigator.onLine) return 'offline'

  markSkipSentry(error)

  if (!options?.immediate && isUserEditing()) {
    toast({
      type: 'info',
      text: 'A new version is available. Click to refresh.',
      duration: 30000,
      onClick: reloadCurrentPage,
    })
    return 'toast'
  }

  reloadCurrentPage()
  return 'reloading'
}
